// Workstream: growth. /events: the event host kit, with an enquiry form that lands in the admin Growth tab.
import { useState, type FormEvent } from 'react';
import { useScrollToTop } from './useScrollToTop';
import { Link } from 'react-router';
import { pageMeta } from '@shared/pages';
import { Footer, TopBar, useDocumentTitle } from '../../components/Chrome';
import { EVENT_TYPE_LABELS, growthApi, type EnquiryInput, type EventType } from './api';

const AUDIENCES: { icon: string; title: string; text: string }[] = [
  { icon: '\u{1F483}', title: 'Sangeets and mehndis', text: 'The icebreaker that gets both families laughing before the dancing starts.' },
  { icon: '\u{1F48D}', title: 'Weddings and receptions', text: 'Something for the cocktail hour, and boxed decks as favours on every table.' },
  { icon: '\u{1F393}', title: 'Student associations', text: 'Diwali night, Eid night, welcome week. Put it on the projector and let the hall vote.' },
  { icon: '\u{1F30F}', title: 'Diaspora and community groups', text: 'Cultural nights, fundraisers and mixers where three generations share a room.' },
  { icon: '\u{1FA94}', title: 'Office Diwali and Eid events', text: 'A friendly way for ERGs to share the culture with colleagues. Clean packs only.' },
];

const KIT: { title: string; text: string }[] = [
  {
    title: 'A custom card pack',
    text: 'We help you write prompts about the couple, the org or the city, mixed in with our Classic cards. Your guests will recognise every joke.',
  },
  {
    title: 'TV and projector setup',
    text: 'Big-screen mode with a QR code to join, synced card flips and the winner reveal. Any laptop and projector will do; we send a checklist.',
  },
  {
    title: 'Printed decks',
    text: 'Boxed decks for tables, favours or raffle prizes, including your custom pack. Great for the people who will ask to play again next week.',
  },
  {
    title: 'A host script',
    text: 'A 15 to 20 minute run of show for your MC: how to explain it in one breath, when to switch to voting, and how to wrap up on a big laugh.',
  },
];

const EMPTY: EnquiryInput = { name: '', email: '', org: '', eventType: 'sangeet', eventDate: '', guests: '', message: '', website: '' };

function EnquiryForm() {
  const [form, setForm] = useState<EnquiryInput>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const set = <K extends keyof EnquiryInput>(key: K, value: EnquiryInput[K]) => setForm((f) => ({ ...f, [key]: value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) return setError('Tell us your name.');
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return setError('We need an email address to reply to.');
    setBusy(true);
    try {
      await growthApi.sendEnquiry(form);
      setSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="panel stack center mk-sent" role="status">
        <p className="mk-sent-icon" aria-hidden="true">
          {'\u{1F64F}'}
        </p>
        <h3>Shukriya, {form.name.split(' ')[0]}!</h3>
        <p className="muted">
          We'll email you at <strong>{form.email}</strong> within a couple of days. In the meantime, try a round with your committee so you know what
          you're in for.
        </p>
        <Link className="btn btn-primary" to="/" data-cta="events_sent_start">
          Start a test game
        </Link>
      </div>
    );
  }

  return (
    <form className="panel stack" onSubmit={submit} noValidate>
      <div className="grid-2">
        <label className="field">
          Your name
          <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} autoComplete="name" maxLength={80} required />
        </label>
        <label className="field">
          Email
          <input
            className="input"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            autoComplete="email"
            inputMode="email"
            maxLength={200}
            required
          />
        </label>
        <label className="field">
          What's the event?
          <select className="input" value={form.eventType} onChange={(e) => set('eventType', e.target.value as EventType)}>
            {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Organisation or couple <span className="hint">Optional</span>
          <input className="input" value={form.org} onChange={(e) => set('org', e.target.value)} autoComplete="organization" maxLength={120} />
        </label>
        <label className="field">
          When is it? <span className="hint">A date or "sometime in November"</span>
          <input className="input" value={form.eventDate} onChange={(e) => set('eventDate', e.target.value)} maxLength={60} />
        </label>
        <label className="field">
          How many guests?
          <select className="input" value={form.guests} onChange={(e) => set('guests', e.target.value)}>
            <option value="">Not sure yet</option>
            <option>Under 30</option>
            <option>30 to 100</option>
            <option>100 to 300</option>
            <option>More than 300</option>
          </select>
        </label>
      </div>
      <label className="field">
        Anything else? <span className="hint">The vibe, the venue, what you'd like from us</span>
        <textarea className="input" value={form.message} onChange={(e) => set('message', e.target.value)} maxLength={2000} rows={4} />
      </label>
      {/* Hidden from people; bots that fill it in are ignored. */}
      <label className="mk-honeypot" aria-hidden="true">
        Website
        <input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set('website', e.target.value)} />
      </label>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <button className="btn btn-primary btn-lg btn-block" disabled={busy} data-cta="events_enquiry_submit">
        {busy ? 'Sending...' : 'Send enquiry'}
      </button>
      <p className="muted small center" style={{ margin: 0 }}>
        We only use your details to reply to you. No mailing lists.
      </p>
    </form>
  );
}

export function EventsPage() {
  const meta = pageMeta('/events')!;
  useDocumentTitle(meta.title);
  useScrollToTop();
  return (
    <>
      <TopBar />
      <main className="page mk">
        <header className="mk-hero">
          <p className="mk-kicker">Event host kit</p>
          <h1>Bring CompleteMasti to your event</h1>
          <p className="mk-lede">
            Sangeets, weddings, student associations, community nights and office Diwali parties. We'll help you run a game that gets a whole hall
            laughing, from the dadis in the front row to the interns at the back.
          </p>
          <div className="mk-actions">
            <a className="btn btn-primary btn-lg" href="#enquire" data-cta="events_hero_enquire">
              Tell us about your event
            </a>
            <Link className="btn btn-outline btn-lg" to="/" data-cta="events_hero_try">
              Try it now
            </Link>
          </div>
        </header>

        <section className="mk-section">
          <h2>Who it's for</h2>
          <div className="mk-grid">
            {AUDIENCES.map((a) => (
              <div key={a.title} className="mk-tile">
                <span className="mk-icon" aria-hidden="true">
                  {a.icon}
                </span>
                <strong>{a.title}</strong>
                <span>{a.text}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mk-section">
          <h2>What's in the kit</h2>
          <ol className="mk-kit">
            {KIT.map((k) => (
              <li key={k.title}>
                <strong>{k.title}</strong>
                <span>{k.text}</span>
              </li>
            ))}
          </ol>
          <p className="muted">
            Playing on phones is always free, at any size. We quote for custom packs, printed decks and hands-on help based on your event. Decks for smaller
            parties are in the <Link to="/shop">shop</Link>.
          </p>
        </section>

        <section className="mk-section">
          <h2>A peek at the host script</h2>
          <blockquote className="mk-script">
            <p>
              "Everyone take out your phone. Scan the code on the screen and type your name. Every round, a question comes up with a blank. You play the
              funniest card from your hand, the whole room votes, and the winner gets a point. Nani, you're on my team."
            </p>
          </blockquote>
        </section>

        <section className="mk-section">
          <h2>How it works</h2>
          <ol className="steps">
            <li>
              <div>
                <strong>Tell us about the event</strong> with the form below.
              </div>
            </li>
            <li>
              <div>
                <strong>We reply within a couple of days</strong> with ideas and a quote for anything that isn't free.
              </div>
            </li>
            <li>
              <div>
                <strong>We build your pack together</strong> and you run a test game with the committee.
              </div>
            </li>
            <li>
              <div>
                <strong>On the night,</strong> your MC follows the script and the room does the rest.
              </div>
            </li>
          </ol>
        </section>

        <section className="mk-section" id="enquire">
          <h2>Tell us about your event</h2>
          <EnquiryForm />
        </section>
      </main>
      <Footer />
    </>
  );
}
